import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Collapse,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { verifyPin, changePin, getAdminStatus, getErrorMessage } from '../provApi';

const BRAND_COLOR = '#1a365d';

interface ProvPinDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Only show change-PIN option when admin session is already active */
  allowChangePin?: boolean;
}

export default function ProvPinDialog({ open, onClose, onSuccess, allowChangePin = false }: ProvPinDialogProps) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [showChangePin, setShowChangePin] = useState(false);
  const [oldPin, setOldPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [changeSuccess, setChangeSuccess] = useState(false);

  const resetForm = () => {
    setPin('');
    setError(null);
    setShowChangePin(false);
    setOldPin('');
    setNewPin('');
    setConfirmPin('');
    setChangeSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleVerify = async () => {
    if (pin.length < 4) {
      setError('PIN-koden måste vara minst 4 siffror');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await verifyPin(pin);
      if (result.success) {
        // Confirm that admin access is actually active (session or stateless token).
        // This avoids the confusing case where PIN verify succeeds but admin requests still 403.
        const status = await getAdminStatus();
        if (!status.isAdmin) {
          setError('PIN-koden verifierades men admin-åtkomst kunde inte aktiveras. Försök igen.');
          return;
        }
        resetForm();
        onSuccess();
      } else {
        setError(result.message || 'Felaktig PIN-kod');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePin = async () => {
    if (oldPin.length < 4) {
      setError('Nuvarande PIN måste vara minst 4 siffror');
      return;
    }
    if (newPin.length < 4) {
      setError('Ny PIN måste vara minst 4 siffror');
      return;
    }
    if (newPin !== confirmPin) {
      setError('Ny PIN och bekräftelse matchar inte');
      return;
    }
    if (!/^\d{4,6}$/.test(newPin)) {
      setError('PIN-koden måste vara 4-6 siffror');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await changePin(oldPin, newPin);
      if (result.success) {
        setChangeSuccess(true);
        setOldPin('');
        setNewPin('');
        setConfirmPin('');
        setTimeout(() => {
          setShowChangePin(false);
          setChangeSuccess(false);
        }, 2000);
      } else {
        setError(result.message || 'Kunde inte ändra PIN');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      if (showChangePin) {
        handleChangePin();
      } else {
        handleVerify();
      }
    }
  };

  const pinInputProps = {
    inputMode: 'numeric' as const,
    pattern: '[0-9]*',
    maxLength: 6,
    style: {
      fontSize: '1.5rem',
      letterSpacing: '0.5rem',
      textAlign: 'center' as const,
      fontFamily: 'monospace',
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: BRAND_COLOR,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LockIcon />
          <Typography variant="h6" component="span">
            {showChangePin ? 'Ändra PIN-kod' : 'Föräldralås'}
          </Typography>
        </Box>
        {!showChangePin && allowChangePin && (
          <IconButton
            onClick={() => setShowChangePin(true)}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.15)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
              p: 1,
            }}
            title="Ändra PIN-kod"
          >
            <SettingsIcon sx={{ fontSize: 24 }} />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Collapse in={!!error}>
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        </Collapse>

        <Collapse in={changeSuccess}>
          <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
            PIN-koden har ändrats!
          </Alert>
        </Collapse>

        {!showChangePin ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
              Ange PIN-kod för att visa facit och hantera provset
            </Typography>
            <TextField
              autoFocus
              fullWidth
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyPress={handleKeyPress}
              placeholder="----"
              inputProps={pinInputProps}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&.Mui-focused fieldset': { borderColor: BRAND_COLOR },
                },
              }}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Ange nuvarande PIN för att ändra till en ny
            </Typography>
            <TextField
              autoFocus
              fullWidth
              type="password"
              label="Nuvarande PIN"
              value={oldPin}
              onChange={(e) => setOldPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyPress={handleKeyPress}
              inputProps={{ ...pinInputProps, style: { ...pinInputProps.style, fontSize: '1.2rem' } }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&.Mui-focused fieldset': { borderColor: BRAND_COLOR } } }}
            />
            <TextField
              fullWidth
              type="password"
              label="Ny PIN (4-6 siffror)"
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyPress={handleKeyPress}
              inputProps={{ ...pinInputProps, style: { ...pinInputProps.style, fontSize: '1.2rem' } }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&.Mui-focused fieldset': { borderColor: BRAND_COLOR } } }}
            />
            <TextField
              fullWidth
              type="password"
              label="Bekräfta ny PIN"
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyPress={handleKeyPress}
              inputProps={{ ...pinInputProps, style: { ...pinInputProps.style, fontSize: '1.2rem' } }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&.Mui-focused fieldset': { borderColor: BRAND_COLOR } } }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 1, gap: 1 }}>
        {showChangePin && (
          <Button
            onClick={() => {
              setShowChangePin(false);
              setOldPin('');
              setNewPin('');
              setConfirmPin('');
              setError(null);
            }}
            variant="text"
            sx={{ mr: 'auto' }}
          >
            Tillbaka
          </Button>
        )}

        <Button
          onClick={handleClose}
          variant="outlined"
          size="large"
          sx={{ minHeight: 52, minWidth: 100, borderRadius: 2 }}
        >
          Avbryt
        </Button>

        <Button
          onClick={showChangePin ? handleChangePin : handleVerify}
          variant="contained"
          size="large"
          disabled={loading || (showChangePin ? (!oldPin || !newPin || !confirmPin) : !pin)}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{
            minHeight: 52,
            minWidth: 140,
            borderRadius: 2,
            bgcolor: BRAND_COLOR,
            '&:hover': { bgcolor: '#0f2744' },
          }}
        >
          {loading ? 'Vänta...' : showChangePin ? 'Ändra PIN' : 'Öppna'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
